import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
} from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoggerService } from 'src/app/logger.service';
import { PostService } from 'src/app/post.service';
import { Post } from '../../types/post.type';

@Component({
  selector: 'app-post-editor',
  templateUrl: './post-editor.component.html',
  styleUrls: ['./post-editor.component.scss'],
})
export class PostEditorComponent implements OnInit, OnDestroy {
  loading: boolean = true;
  saving: boolean = false;
  publishing: boolean = false;
  unpublishing: boolean = false;
  saveError: string = '';
  post?: Post;

  form: FormGroup = this.fb.group({
    post_id: [null, [Validators.required]],
    title: ['', Validators.required],
    description: [''],
    draft: ['', Validators.required],
  });

  private subscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private logger: LoggerService,
    private fb: FormBuilder,
    private postService: PostService,
    private router: Router,
    private _snackBar: MatSnackBar
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.route.params.subscribe((params) => {
        this.logger.log(params);
        if (params.id) {
          this.getPost(params.id);
        } else {
          this.router.navigate(['/']);
        }
      })
    );
  }

  get isBusy(): boolean {
    return this.saving || this.publishing || this.unpublishing;
  }

  get saveStateLabel(): string {
    if (this.saving) return '正在保存';
    if (this.saveError) return '保存失败';
    return this.form.dirty ? '有未保存的修改' : '已保存';
  }

  get saveStateIcon(): string {
    if (this.saving) return 'sync';
    if (this.saveError) return 'error_outline';
    return this.form.dirty ? 'edit' : 'cloud_done';
  }

  goBack(): void {
    this.router.navigate(['/']);
  }

  handleSave(): void {
    if (this.form.invalid || this.isBusy) return;
    this.savePost(this.form.getRawValue(), true);
  }

  getPost(post_id: string) {
    return this.postService.getDraft(post_id).subscribe(
      (res) => {
        const { post_id, title, description, draft } = res;
        this.form.setValue(
          {
            post_id,
            title,
            description,
            draft,
          },
          { emitEvent: false }
        );
        this.form.markAsPristine();
        this.loading = false;
        this.post = res;

        this.subscriptions.push(
          this.form.valueChanges
            .pipe(
              debounceTime(1500),
              distinctUntilChanged(
                (previous, current) =>
                  JSON.stringify(previous) === JSON.stringify(current)
              )
            )
            .subscribe((post) => {
              if (post.post_id && this.form.valid && !this.isBusy) {
                this.savePost(post, false);
              }
            })
        );
      },
      (error) => {
        this.logger.error(error);
        this.loading = false;
        this._snackBar.open('文章加载失败，请返回后重试。', '关闭', {
          duration: 5000,
        });
      }
    );
  }

  savePost(post: Post, showConfirmation: boolean = true) {
    if (this.saving) return;

    const savedSnapshot = JSON.stringify(post);
    this.saving = true;
    this.saveError = '';
    if (showConfirmation) this._snackBar.open('正在保存…');

    this.postService
      .updateDraft(post)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe(
        (res) => {
          this.post = { ...this.post, ...res };
          if (JSON.stringify(this.form.getRawValue()) === savedSnapshot) {
            this.form.markAsPristine();
          }
          if (showConfirmation) {
            this._snackBar.dismiss();
            this._snackBar.open('已保存', undefined, { duration: 1200 });
          }
        },
        (error) => {
          this.logger.error(error);
          this.saveError = '保存失败';
          this._snackBar.dismiss();
          this._snackBar.open('保存失败，请检查网络后重试。', '关闭', {
            duration: 5000,
          });
        }
      );
  }

  publishPost() {
    if (!this.post || this.form.invalid || this.isBusy) return;

    const draft = this.form.getRawValue() as Post;
    this.publishing = true;
    this.saveError = '';
    this._snackBar.open('正在保存并发布…');

    this.postService
      .updateDraft(draft)
      .pipe(
        switchMap((savedPost) => {
          this.post = { ...this.post, ...savedPost };
          return this.postService.publishPost(this.post);
        }),
        finalize(() => (this.publishing = false))
      )
      .subscribe(
        (res) => {
          this._snackBar.dismiss();
          if (res.success) {
            this.post!.is_published = true;
            this.form.markAsPristine();
            this._snackBar.open('文章已发布', undefined, { duration: 1600 });
          } else {
            this._snackBar.open(res.message || '发布失败，请重试。', '关闭', {
              duration: 5000,
            });
          }
        },
        (error) => {
          this.logger.error(error);
          this.saveError = '发布失败';
          this._snackBar.dismiss();
          this._snackBar.open('发布失败，请检查网络后重试。', '关闭', {
            duration: 5000,
          });
        }
      );
  }

  unpublishPost() {
    if (!this.post || this.isBusy) return;

    this.unpublishing = true;
    this._snackBar.open('正在取消发布…');
    this.postService
      .unpublishPost(this.post)
      .pipe(finalize(() => (this.unpublishing = false)))
      .subscribe(
        (res) => {
          this._snackBar.dismiss();
          if (res.success) {
            this.post!.is_published = false;
            this._snackBar.open('已转为草稿', undefined, { duration: 1600 });
          } else {
            this._snackBar.open(
              res.message || '取消发布失败，请重试。',
              '关闭',
              { duration: 5000 }
            );
          }
        },
        (error) => {
          this.logger.error(error);
          this._snackBar.dismiss();
          this._snackBar.open('取消发布失败，请检查网络后重试。', '关闭', {
            duration: 5000,
          });
        }
      );
  }
}
